import type { DatadogRumConfig } from '@blockit/config/web';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogRum, type RumEvent } from '@datadog/browser-rum-slim';

const RUM_SERVICE = 'blockit-web';
const RUM_SITE = 'datadoghq.com';
const DEPLOYED_VERSION_META = 'meta[name="dd-version"]';

// Written into index.html by scripts/render-web-build.sh after the cached turbo build, so the
// bundle stays commit-independent. Absent in local dev.
const readDeployedVersion = (): string | null =>
  document.querySelector<HTMLMetaElement>(DEPLOYED_VERSION_META)?.content || null;

const QUERY_OR_FRAGMENT = /[?#]/;
// Stops before a trailing `:line:col` so stack frames keep their position for source mapping.
const URL_QUERY_IN_TEXT = /(https?:\/\/[^\s?#"'<>()]+)[?#][^\s"'<>()]*?(?=(?::\d+:\d+)?(?:[\s"'<>()]|$))/g;

// OAuth callbacks (Google, Microsoft, Slack, Zoom, WorkOS) land with live authorization codes and
// PKCE state in the query string, and RUM starts before the callback page consumes them.
const stripQueryAndFragment = (url: string): string => {
  if (!URL.canParse(url)) {
    return url.split(QUERY_OR_FRAGMENT, 1)[0];
  }
  const { origin, pathname } = new URL(url);
  return `${origin}${pathname}`;
};

const stripUrlQueriesInText = (text: string): string => text.replace(URL_QUERY_IN_TEXT, '$1');

// The SDK only applies edits made to the event object it passes in.
const redactUrls = (event: RumEvent): boolean => {
  event.view.url = stripQueryAndFragment(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = stripQueryAndFragment(event.view.referrer);
  }
  if (event.type === 'resource') {
    event.resource.url = stripQueryAndFragment(event.resource.url);
  }
  if (event.type === 'error') {
    event.error.message = stripUrlQueriesInText(event.error.message);
    if (event.error.stack) {
      event.error.stack = stripUrlQueriesInText(event.error.stack);
    }
    if (event.error.resource) {
      event.error.resource.url = stripQueryAndFragment(event.error.resource.url);
    }
  }
  return true;
};

export const initRum = (config: DatadogRumConfig | null) => {
  if (!config) {
    return;
  }

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: RUM_SITE,
    service: RUM_SERVICE,
    env: config.env,
    version: readDeployedVersion(),
    beforeSend: redactUrls,
    plugins: [reactPlugin({ router: true })],
  });
};
